import { useEffect, useState } from "react";
import { ErrorState, LoadingSkeleton } from "@a2z/ui";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { PageTransition } from "@/shared/components/PageTransition";
import { MobileScreenHeader } from "@/shared/components/MobileScreenHeader";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { PersonalInfoModal } from "@/modules/profile/components/PersonalInfoModal";
import { PasswordModal } from "@/modules/profile/components/PasswordModal";

const ps = keys.seller.profileSettings;

type ProfilePageProps = {
  storeSlug: string;
};

// The profile page is now a minimal "trigger surface" — primarily for mobile,
// where the desktop avatar dropdown isn't available. It mirrors the same two
// items that live in the avatar menu: Personal info, Password. Each opens
// the same modal as the dropdown does on desktop.
export function ProfilePage({ storeSlug }: ProfilePageProps) {
  const t = useTranslations();
  const isMobile = useIsMobile();
  const { data, isLoading, error, refetch } = useSessionQuery({ storeSlug });
  const seller = data?.seller ?? null;

  const [open, setOpen] = useState<"personal-info" | "password" | null>(null);

  const accountTitleLabel = t(keys.seller.accountTitle);
  const profileSubtitleLabel = t(keys.seller.profileSubtitle);

  // Same page-chrome pattern as Store Settings.
  const { setHero, setHeroAux } = usePageChrome();
  useEffect(() => {
    if (isMobile) {
      setHero(null);
      return () => setHero(null);
    }
    setHero(
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
            {accountTitleLabel}
          </h1>
          <p className="max-w-[560px] text-meta text-[color:var(--text-muted)]">
            {profileSubtitleLabel}
          </p>
        </div>
      </div>,
    );
    return () => setHero(null);
  }, [setHero, isMobile, accountTitleLabel, profileSubtitleLabel]);
  useEffect(() => {
    setHeroAux(null);
    return () => setHeroAux(null);
  }, [setHeroAux]);

  if (isLoading && !seller) {
    return <LoadingSkeleton variant="card" rows={2} />;
  }
  if (error) {
    return <ErrorState onRetry={refetch} />;
  }

  return (
    <PageTransition className="w-full">
      {isMobile ? (
        <MobileScreenHeader
          theme="light"
          surface="var(--shell)"
          title={accountTitleLabel}
          ariaLabel={accountTitleLabel}
          heading={
            <h1 className="m-0 text-[23px] font-bold leading-tight tracking-[-0.025em] text-[color:var(--text-primary)]">
              {accountTitleLabel}
            </h1>
          }
          subtitle={
            <div className="mt-[3px] text-meta text-[color:var(--text-muted)]">
              {profileSubtitleLabel}
            </div>
          }
        />
      ) : null}

      <section
        className="bg-[color:var(--canvas)]"
        style={{
          padding: isMobile ? "12px 16px 32px" : "24px 32px 80px",
          minHeight: isMobile ? undefined : "100vh",
        }}
      >
        <div
          className="mx-auto flex flex-col gap-3"
          style={{ maxWidth: 560 }}
        >
          <Trigger
            label={t(ps.personalInfo)}
            hint={t(ps.personalInfoDesc)}
            icon={<UserIcon />}
            onClick={() => setOpen("personal-info")}
          />
          <Trigger
            label={t(ps.password)}
            hint={t(ps.passwordDesc)}
            icon={<LockIcon />}
            onClick={() => setOpen("password")}
          />
        </div>
      </section>

      <PersonalInfoModal
        isOpen={open === "personal-info"}
        onClose={() => setOpen(null)}
        storeSlug={storeSlug}
        seller={seller}
      />
      <PasswordModal
        isOpen={open === "password"}
        onClose={() => setOpen(null)}
        storeSlug={storeSlug}
      />
    </PageTransition>
  );
}

function Trigger({
  label,
  hint,
  icon,
  onClick,
}: {
  label: string;
  hint: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4 text-start transition-colors hover:bg-[color:var(--surface-alt)]"
    >
      <span
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[color:var(--surface-alt)] text-[color:var(--text-secondary)]"
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
        <span className="text-[14px] font-semibold text-[color:var(--text-primary)]">
          {label}
        </span>
        <span className="text-[12px] text-[color:var(--text-muted)]">
          {hint}
        </span>
      </span>
      <ChevronEnd />
    </button>
  );
}

const SVG_PROPS = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function UserIcon() {
  return (
    <svg {...SVG_PROPS}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg {...SVG_PROPS}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function ChevronEnd() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-[color:var(--text-muted)] rtl:rotate-180"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
